import { apiClient } from '@/api/client'
import { ApiError, parseProblemDetails } from '@/api/problemDetails'
import type { components } from '@/api/schema'

import type {
  AgentExecutionAggregates,
  AnalyticsOverview,
  ChannelHealthEntry,
  InboundEventAggregates,
  KnowledgeStatusSummary,
  UsageRollup,
} from './types'

/** `int32`/`int64` chegam como `number | string` (precisao). As grandezas de analytics cabem no intervalo seguro. */
function toNumber(value: number | string): number {
  return typeof value === 'number' ? value : Number(value)
}

function toNullableNumber(value: number | string | null): number | null {
  return value === null ? null : toNumber(value)
}

function unexpectedFormatError(status: number): ApiError {
  return new ApiError({
    title: 'A resposta da Api nao tem o formato esperado.',
    status,
    errorCode: 'internal_error',
    correlationId: '',
  })
}

function mapAgent(
  dto: components['schemas']['AgentExecutionAggregates'],
): AgentExecutionAggregates {
  return {
    total: toNumber(dto.total),
    succeeded: toNumber(dto.succeeded),
    failed: toNumber(dto.failed),
    suggestionsToHuman: toNumber(dto.suggestionsToHuman),
    autoReplies: toNumber(dto.autoReplies),
    promptTokens: toNumber(dto.promptTokens),
    completionTokens: toNumber(dto.completionTokens),
    averageDurationMs: toNullableNumber(dto.averageDurationMs),
    p95DurationMs: toNullableNumber(dto.p95DurationMs),
    byMode: dto.byMode.map((m) => ({ mode: m.mode, count: toNumber(m.count) })),
    byModel: dto.byModel.map((m) => ({
      provider: m.provider,
      model: m.model,
      count: toNumber(m.count),
      promptTokens: toNumber(m.promptTokens),
      completionTokens: toNumber(m.completionTokens),
    })),
    daily: dto.daily.map((d) => ({
      day: d.day,
      total: toNumber(d.total),
      succeeded: toNumber(d.succeeded),
      failed: toNumber(d.failed),
      promptTokens: toNumber(d.promptTokens),
      completionTokens: toNumber(d.completionTokens),
    })),
  }
}

function mapInbound(dto: components['schemas']['InboundEventAggregates']): InboundEventAggregates {
  return {
    total: toNumber(dto.total),
    byStatus: dto.byStatus.map((s) => ({ status: s.status, count: toNumber(s.count) })),
    failuresByErrorCode: dto.failuresByErrorCode.map((f) => ({
      errorCode: f.errorCode,
      count: toNumber(f.count),
    })),
    averageProcessingMs: toNullableNumber(dto.averageProcessingMs),
  }
}

function mapKnowledge(
  dto: components['schemas']['KnowledgeStatusSummary'],
): KnowledgeStatusSummary {
  return {
    totalDocuments: toNumber(dto.totalDocuments),
    pending: toNumber(dto.pending),
    processing: toNumber(dto.processing),
    ready: toNumber(dto.ready),
    failed: toNumber(dto.failed),
    totalChunks: toNumber(dto.totalChunks),
  }
}

function mapChannel(dto: components['schemas']['ChannelHealthEntry']): ChannelHealthEntry {
  return { name: dto.name, channelType: dto.channelType, status: dto.status }
}

function mapUsage(dto: components['schemas']['UsageRollup']): UsageRollup {
  return {
    agents: toNumber(dto.agents),
    channels: toNumber(dto.channels),
    users: toNumber(dto.users),
    documents: toNumber(dto.documents),
    aiExecutions: toNumber(dto.aiExecutions),
    promptTokens: toNumber(dto.promptTokens),
    completionTokens: toNumber(dto.completionTokens),
    totalTokens: toNumber(dto.totalTokens ?? 0),
  }
}

/** Busca a visao geral de analytics (dados reais) do tenant na janela pedida (dias). */
export async function getAnalyticsOverview(
  tenantId: string,
  days: number,
): Promise<AnalyticsOverview> {
  const result = await apiClient.GET('/api/v1/tenants/{tenantId}/analytics', {
    params: { path: { tenantId }, query: { days } },
  })

  if (!result.response.ok) {
    throw new ApiError(parseProblemDetails(result.error, result.response.status))
  }

  if (result.data === undefined) {
    throw unexpectedFormatError(result.response.status)
  }

  return {
    window: {
      fromUtc: result.data.window.fromUtc,
      toUtc: result.data.window.toUtc,
      days: toNumber(result.data.window.days),
    },
    agentExecutions: mapAgent(result.data.agentExecutions),
    inbound: mapInbound(result.data.inbound),
    knowledge: mapKnowledge(result.data.knowledge),
    channelHealth: result.data.channelHealth.map(mapChannel),
    usage: mapUsage(result.data.usage),
  }
}
